import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import ResponsibilityItem from '../components/disclaimer/ResponsibilityItem';
import NotResponsibleItem from '../components/disclaimer/NotResponsibleItem';

const DisclaimerPage = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="disclaimer-page">
      <SEO
        title={t('Affiliate Disclaimer - U&Fashion', 'Affiliate Disclaimer - U&Fashion')}
        description={t(
          'Belangrijke informatie over onze affiliate partnerships en verantwoordelijkheden',
          'Important information about our affiliate partnerships and responsibilities'
        )}
      />

      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl font-bold mb-4">
            {t('Affiliate Disclaimer', 'Affiliate Disclaimer')}
          </h1>
          <p className="text-xl text-white/90">
            {t(
              'Transparantie over onze partnerships en verantwoordelijkheden',
              'Transparency about our partnerships and responsibilities'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
          {/* Dutch Version */}
          {language === 'nl' ? (
            <>
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Wat zijn Affiliate Links?
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion.eu werkt samen met verschillende online retailers en merken via affiliate netwerken 
                  zoals TradeTracker, Awin en andere platforms. Wanneer u op een productlink klikt en een aankoop 
                  doet, kunnen wij een kleine commissie ontvangen. Dit heeft geen invloed op de prijs die u betaalt.
                </p>
              </section>

              <section className="bg-pink-50 p-6 rounded-lg border-l-4 border-pink-500">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Onze Verantwoordelijkheid
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Als affiliate partner is onze rol beperkt tot het <strong>presenteren en aanbieden van 
                  productinformatie en links</strong> naar de websites van onze partners.
                </p>
                <div className="space-y-3">
                  <ResponsibilityItem>
                    Wij bieden u een overzicht van beschikbare fashion producten
                  </ResponsibilityItem>
                  <ResponsibilityItem>
                    Wij verzamelen productinformatie van onze partners
                  </ResponsibilityItem>
                  <ResponsibilityItem>
                    Wij faciliteren de doorverwijzing naar de website van de verkoper
                  </ResponsibilityItem>
                </div>
              </section>

              <section className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Waar Wij NIET Verantwoordelijk Voor Zijn
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is <strong>NIET</strong> verantwoordelijk voor:
                </p>
                <div className="space-y-3">
                  <NotResponsibleItem 
                    title="Fouten op de website van de adverteerder"
                    description="Inclusief verkeerde prijzen, productbeschrijvingen, of technische problemen op hun platform"
                  />
                  <NotResponsibleItem 
                    title="Levering en verzending"
                    description="Vertragingen, beschadigde pakketten, of verloren zendingen vallen onder de verantwoordelijkheid van de verkoper"
                  />
                  <NotResponsibleItem 
                    title="Retourzendingen en terugbetalingen"
                    description="Het retourbeleid en afhandeling van retourzendingen wordt bepaald door de verkoper, niet door ons"
                  />
                  <NotResponsibleItem 
                    title="Productkwaliteit en beschikbaarheid"
                    description="De kwaliteit, maten, kleuren en voorraad worden beheerd door de verkoper"
                  />
                  <NotResponsibleItem 
                    title="Klantenservice van de verkoper"
                    description="Voor vragen over uw bestelling, neem rechtstreeks contact op met de verkoper"
                  />
                  <NotResponsibleItem 
                    title="Betalingen en transacties"
                    description="Alle financiële transacties vinden plaats tussen u en de verkoper"
                  />
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Belangrijke Informatie
                </h2>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Wanneer u op een productlink klikt, wordt u doorverwezen naar de website van onze partnerretailer. 
                    Vanaf dat moment zijn de <strong>algemene voorwaarden, het privacybeleid en het retourbeleid 
                    van die retailer van toepassing</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Wij raden u aan om deze voorwaarden zorgvuldig door te lezen voordat u een aankoop doet.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Heeft u een Probleem?
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Voor vragen of klachten over:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Een specifieke bestelling → Neem contact op met de verkoper waar u besteld heeft</li>
                  <li>Levering of retourzending → Neem contact op met de klantenservice van de verkoper</li>
                  <li>Productinformatie op onze site → U kunt ons bereiken via de contactpagina</li>
                </ul>
              </section>

              <section className="bg-gray-100 p-6 rounded-lg">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Laatste update:</strong> {new Date().toLocaleDateString('nl-NL')}<br/>
                  Deze disclaimer kan van tijd tot tijd worden bijgewerkt. We raden u aan deze pagina regelmatig 
                  te raadplegen voor eventuele wijzigingen.
                </p>
              </section>
            </>
          ) : (
            /* English Version */
            <>
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  What are Affiliate Links?
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion.eu partners with various online retailers and brands through affiliate networks 
                  such as TradeTracker, Awin, and other platforms. When you click on a product link and make 
                  a purchase, we may receive a small commission. This does not affect the price you pay.
                </p>
              </section>

              <section className="bg-pink-50 p-6 rounded-lg border-l-4 border-pink-500">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Our Responsibility
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As an affiliate partner, our role is limited to <strong>presenting and offering 
                  product information and links</strong> to our partners' websites.
                </p>
                <div className="space-y-3">
                  <ResponsibilityItem>
                    We provide you with an overview of available fashion products
                  </ResponsibilityItem>
                  <ResponsibilityItem>
                    We collect product information from our partners
                  </ResponsibilityItem>
                  <ResponsibilityItem>
                    We facilitate the referral to the seller's website
                  </ResponsibilityItem>
                </div>
              </section>

              <section className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  What We Are NOT Responsible For
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  U&Fashion.eu is <strong>NOT</strong> responsible for:
                </p>
                <div className="space-y-3">
                  <NotResponsibleItem 
                    title="Errors on the advertiser's website"
                    description="Including incorrect prices, product descriptions, or technical issues on their platform"
                  />
                  <NotResponsibleItem 
                    title="Delivery and shipping"
                    description="Delays, damaged packages, or lost shipments are the responsibility of the seller"
                  />
                  <NotResponsibleItem 
                    title="Returns and refunds"
                    description="The return policy and handling of returns is determined by the seller, not by us"
                  />
                  <NotResponsibleItem 
                    title="Product quality and availability"
                    description="Quality, sizes, colors, and stock are managed by the seller"
                  />
                  <NotResponsibleItem 
                    title="Seller's customer service"
                    description="For questions about your order, please contact the seller directly"
                  />
                  <NotResponsibleItem 
                    title="Payments and transactions"
                    description="All financial transactions take place between you and the seller"
                  />
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Important Information
                </h2>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    When you click on a product link, you will be redirected to our partner retailer's website. 
                    From that moment, the <strong>terms and conditions, privacy policy, and return policy 
                    of that retailer apply</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    We recommend that you carefully read these terms before making a purchase.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Have a Problem?
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For questions or complaints about:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>A specific order → Contact the seller where you placed your order</li>
                  <li>Delivery or returns → Contact the seller's customer service</li>
                  <li>Product information on our site → You can reach us via the contact page</li>
                </ul>
              </section>

              <section className="bg-gray-100 p-6 rounded-lg">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US')}<br/>
                  This disclaimer may be updated from time to time. We recommend checking this page 
                  regularly for any changes.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisclaimerPage;
